import { Dialog, DialogProps } from '@mui/material';
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

type DialogContextProps = {
  closeDialog: () => void;
  prepareDialog: (props: {
    content?: React.ReactNode;
    dialogProps: Partial<DialogProps>;
  }) => void;
  showDialog: () => void;
  updateDialogContent: (content: React.ReactNode) => void;
};

export const DialogContext = createContext<DialogContextProps | null>(null);

export const DialogProvider: FunctionComponent<PropsWithChildren> = (props) => {
  const { children } = props;
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<React.ReactNode>();
  const [dialogProps, setDialogProps] = useState<Partial<DialogProps>>();

  const updateDialogContent = (content: React.ReactNode) => {
    setContent(content);
  };

  const prepareDialog: DialogContextProps['prepareDialog'] = (props) => {
    const { content, dialogProps } = props;
    updateDialogContent(content);
    setDialogProps(dialogProps);
  };

  const onCloseModal: DialogProps['onClose'] = useCallback(
    (event: any, reason: any) => {
      if (dialogProps?.onClose) {
        dialogProps.onClose(event, reason);
      }
      setOpen(false);
      setContent(undefined);
      setDialogProps(undefined);
    },
    [dialogProps],
  );

  const showDialog = () => setOpen(true);
  const closeDialog = () => onCloseModal({}, 'escapeKeyDown');

  const context: DialogContextProps = {
    closeDialog,
    prepareDialog,
    showDialog,
    updateDialogContent,
  };

  return (
    <DialogContext.Provider value={context}>
      {content && (
        <Dialog {...dialogProps} open={open} onClose={onCloseModal}>
          {content}
        </Dialog>
      )}
      {children}
    </DialogContext.Provider>
  );
};

export const useDialog = () => useContext(DialogContext) as DialogContextProps;
